<template>
  <sp-select
    ref="container"
    inputmode="search"
    :no-data-text="noDataText"
    :items="items"
    :item-title="itemTitle"
    :hide-no-data="!hasSearchInput"
    :menu="hasSearchInput"
    :disabled="disabled"
    :value.prop="model"
    @input="onInput"
    @text-input="onSearchInput"
    @click-append="$emit('click-append')"
    @click-prepend="$emit('click-prepend')"
  ></sp-select>
</template>

<script setup>
import { onClickOutside } from "@vueuse/core";
import { computed, ref, toRef, useAttrs, useTemplateRef, watch } from "vue";
import { useExpose } from "#ce/composables/useExpose";
import { useForm } from "#ce/composables/useForm";
import { useHostEmit } from "#ce/composables/useHostEmit";
import { t } from "#ce/shared/forms-i18n";
import { useFormControl } from "#ce/composables/useFormControl";
import { isNoSelection, isPresent, toBoolean } from "#ce/utils/props";
import { props as autocompleteProps } from "./props.js";
import { formAssociatedHost } from "#ce/framework/utils";

// sp-autocomplete is a genuine form control: match :disabled and respond to <fieldset disabled>, forwarding to its select.
defineOptions(formAssociatedHost());

const emit = defineEmits(["input", "click-append", "click-prepend", "update-search"]);
const { emitBubbling } = useHostEmit();

const props = defineProps(autocompleteProps);

// Disabled is governed by the form-control composable: presence-based own
// attribute plus ancestor <fieldset disabled>, forwarded to the inner sp-select.
const { disabled } = useFormControl({ disabled: toRef(props, "disabled") });

// The `value` attribute seeds the selection and names the value a reset restores; the `value`
// property is the live selection. A declared prop would install a non-configurable host accessor
// that returns the seed forever.
const attrs = useAttrs();
const defaultValue = ref(attrs.value ?? null);
const model = ref(defaultValue.value);

watch(
  () => attrs.value,
  (value) => {
    defaultValue.value = value ?? null;
    model.value = defaultValue.value;
  },
);

const { exposeProperties } = useExpose();

exposeProperties({
  value: {
    configurable: true,
    get: () => model.value,
    set: (value) => {
      model.value = isNoSelection(value) ? null : value;
    },
  },
  defaultValue: {
    configurable: true,
    get: () => defaultValue.value,
  },
});

// The inner sp-select sits in the shadow root, where the outer form cannot see it, so the element
// submits the selection through ElementInternals under its own name.
const { mirrorFormValue, mirrorValueMissing, onFormReset } = useForm();
const formValue = computed(() => (model.value === undefined || model.value === null ? "" : String(model.value)));

mirrorFormValue(formValue);

onFormReset(() => (model.value = defaultValue.value));

// A required autocomplete is satisfied only once something is selected.
const isRequired = computed(() => isPresent(props.required));
const validationMessage = computed(() => props.requiredMessage || t("select_value_missing"));

mirrorValueMissing(() => isRequired.value && formValue.value === "", validationMessage);

const search = ref(props.search);
watch(
  () => props.search,
  (value) => (search.value = value),
);

const hasSearchInput = computed(() => search.value?.length > 0);

const noFilter = computed(() => toBoolean(props.noFilter));

const items = computed(() => {
  if (noFilter.value || !hasSearchInput.value) {
    return props.items;
  }
  return props.items.filter((item) => {
    const title = item?.[props.itemTitle] ?? item;
    return title?.toLowerCase().includes(search.value?.toLowerCase?.());
  });
});

// The only path that emits `input`, matching a native control, which reports user input alone: a
// value carried in by the attribute, the property or a reset is mirrored silently.
function onInput({ detail }) {
  const [value] = detail;
  model.value = value;
  emitBubbling("input", value);
}

// The only path that emits `update-search`: a `search` prop sync and the click-outside clear
// mirror silently, so hosts can treat every `update-search` as the user typing.
function onSearchInput({ detail }) {
  const [value] = detail;
  search.value = value;
  emit("update-search", value);
}

const container = useTemplateRef("container");
onClickOutside(container, () => {
  if (model.value === null) {
    search.value = null;
  }
});
</script>

<style>
:host {
  display: block;
}
</style>
